/** Inventory edit / delete building blocks: access hook, reason-confirm dialog, row action buttons. */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Pencil, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export const errMsg = (e) => { const d = e.response?.data?.detail; return typeof d === "string" ? d : d?.message || e.message; };
let cache = null;
export function useInvAccess() {
  const [a, setA] = useState(cache || { can_edit: false, can_delete: false });
  useEffect(() => { if (cache) return; api.get("/inventory-admin/access").then((r) => { cache = r.data; setA(r.data); }).catch(() => {}); }, []);
  return a;
}

export function RowActions({ access, onEdit, onDelete, testid }) {
  if (!access.can_edit && !access.can_delete) return null;
  return (
    <span className="inline-flex items-center gap-1">
      {access.can_edit && onEdit && <button type="button" onClick={onEdit} title="Edit" className="grid h-7 w-7 place-items-center rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50" data-testid={`${testid}-edit`}><Pencil className="h-3.5 w-3.5" /></button>}
      {access.can_delete && onDelete && <button type="button" onClick={onDelete} title="Delete (archive)" className="grid h-7 w-7 place-items-center rounded-md border border-rose-200 text-rose-700 hover:bg-rose-50" data-testid={`${testid}-delete`}><Trash2 className="h-3.5 w-3.5" /></button>}
    </span>
  );
}

export function ReasonField({ value, onChange, testid }) {
  return (
    <label className="block space-y-1"><span className="text-[11px] font-semibold text-slate-600">Reason for change *</span>
      <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={2} placeholder="e.g. serial mis-typed at receiving" className="w-full rounded-md border border-slate-300 p-2 text-[13px]" data-testid={testid} />
    </label>
  );
}

export function ReasonDialog({ open, title, description, confirm = "Delete", onConfirm, onClose, testid = "reason-dialog" }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setReason(""); }, [open]);
  const go = async () => { setBusy(true); try { await onConfirm(reason); } finally { setBusy(false); } };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid={testid}>
        <DialogHeader><DialogTitle>{title}</DialogTitle>{description && <DialogDescription>{description}</DialogDescription>}</DialogHeader>
        <ReasonField value={reason} onChange={setReason} testid={`${testid}-reason`} />
        <div className="text-[11px] text-slate-500">The record is archived, not erased. It stays in the audit trail and movement history.</div>
        <div className="flex justify-end gap-2"><button onClick={onClose} className="h-9 rounded-full border border-slate-300 px-4 text-[13px]" data-testid={`${testid}-cancel`}>Cancel</button>
          <button onClick={go} disabled={reason.trim().length < 3 || busy} className="h-9 rounded-full bg-rose-600 px-4 text-[13px] font-semibold text-white disabled:opacity-50" data-testid={`${testid}-confirm`}>{busy ? "Working…" : confirm}</button></div>
      </DialogContent>
    </Dialog>
  );
}

export function EditShell({ open, title, onClose, onSave, busy, reason, setReason, children, testid }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid={testid}>
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">{children}</div>
        <ReasonField value={reason} onChange={setReason} testid={`${testid}-reason`} />
        <div className="flex justify-end gap-2"><button onClick={onClose} className="h-9 rounded-full border border-slate-300 px-4 text-[13px]" data-testid={`${testid}-cancel`}>Cancel</button>
          <button onClick={onSave} disabled={reason.trim().length < 3 || busy} className="h-9 rounded-full bg-slate-900 px-4 text-[13px] font-semibold text-white disabled:opacity-50" data-testid={`${testid}-save`}>{busy ? "Saving…" : "Save changes"}</button></div>
      </DialogContent>
    </Dialog>
  );
}

export function F({ l, children, wide }) {
  return <label className={`space-y-1 ${wide ? "col-span-2" : ""}`}><span className="text-[11px] text-slate-500">{l}</span>{children}</label>;
}
export const inp = "h-9 w-full rounded-md border border-slate-300 px-2 text-[13px]";
